import React, { useContext, useMemo } from 'react';
import { Field, Form, FormElement } from '@progress/kendo-react-form';
import { ITicket } from '../../../../types/Ticket/ITicket';
import { useReduxDispatch, useReduxSelector } from '../../../../redux/hooks';
import { TicketApi } from '../../../../redux/api/TicketApi';
import * as ActionSlice from '../../../../slices/ActionSlice';
import ActionNote from '../../../Action/ActionNote/ActionNote';
import { Button, SplitButton } from '@progress/kendo-react-buttons';
import { RevUserContext, RevUserContextType } from '../../../../context/UserContext';
import { Card, CardBody, CardHeader } from '@progress/kendo-react-layout';
import * as svg from '@progress/kendo-svg-icons';
import { Link } from 'react-router-dom';
import { PartialSpinny } from '../../../Nav/Spinny';
import { IActionDTO } from '../../../../types/Action/IAction';
import AINotes from './AINotes';
import FileUpload from '../../../Attachment/FileUpload';
import { CardFooter } from 'react-bootstrap';
import { FileType } from '../../../../types/File/FileType';
import { FileApi } from '../../../../redux/api/FileApi';
import { isObject } from 'lodash';
import { nanoid } from 'nanoid';
import AddKBButton from './Tools/KB/AddKBButton';
import { useCloseTicketLogic } from '../../../../hooks/useCloseTicketLogic';
import EffortExceededWarning from '../../../Gameplan/EffortExceededWarning';
import { SvgIcon } from '@progress/kendo-react-common';

interface AddActionProps {
    ticket: ITicket
    onsubmitted?: () => void
    onclosed?: () => void
    showHeader?: boolean
    showSaveButton?: boolean
    actionId?: number
}

const AddAction: React.FC<AddActionProps> = ({ actionId, ticket, onsubmitted, onclosed, showHeader, showSaveButton }) => {
    const { profile } = React.useContext<RevUserContextType>(RevUserContext)
    const { data: isFileActiveInternal } = FileApi.useIsActiveQuery(FileType.Call_Action_Internal)
    const { data: isFileActiveExternal } = FileApi.useIsActiveQuery(FileType.Call_Action_External)
    const isFileActive = (profile?.Features.External_Interface && isFileActiveExternal) || (!profile?.Features.External_Interface && isFileActiveInternal);
    const { action: newAction } = useReduxSelector(state => state.actionDetails);
    const [postAction, { isLoading: savingAction }] = TicketApi.useCreateActionMutation();
    const [updateAction, { isLoading: updatingAction }] = TicketApi.useUpdateActionMutation();
    const dispatch = useReduxDispatch();
    const [apiError, setApiError] = React.useState<string>()
    //Get existing action
    const { data: action, isFetching: fetchingAction } = TicketApi.useGetActionQuery({ actionId: actionId, ticketId: ticket?.ID }, { skip: (!actionId || !ticket?.ID) });
    //Create a unique form name
    const formName = useMemo(() => 'new-action_' + nanoid(), [actionId]);
    const { closeTicket, isClosing, error: closeError } = useCloseTicketLogic(profile);

    //const { data: newTicket, refetch } = TicketApi.useGetTicketDetailsQuery({ ticketId: ticket?.ID ?? 0 }, { skip: !ticket?.ID });

    //Put the existing action in state when it is fetched from the API
    React.useEffect(() => {
        if (action) {
            //Update the HTML action if it is blank (some system generated notes)
            dispatch(ActionSlice.SetAction({ ...action, HTML_Notes: action.HTML_Notes || action.Action_Notes, Action_Dt: new Date(action.Action_Dt + 'Z') }));
        }
    }, [action])

    const onCloseClicked = () => {
        if (newAction?.HTML_Notes?.length > 0 && !window.confirm('You have unsaved changes. Are you sure you want to cancel this Action note?')) return;

        onclosed();
    }


    const handleActionSubmit = (saveAndClose: boolean) => {

        let actionTypeId = newAction.Action_Type_Id ?? (profile.Features.External_Interface ? 8 : 1);
        let saveAction = { ...newAction, Call_Id: ticket.ID, Notes: newAction.HTML_Notes, Action_Type_Id: actionTypeId, Contact_Id: newAction.Staff_Id } as IActionDTO
        let updateProps = { Id: saveAction.ID, ticketId: saveAction.Call_Id, action: saveAction };

        //Add or update
        newAction.ID === 0 ?
            postAction(saveAction).unwrap().then(() => {
                //Reset the action in state
                dispatch(ActionSlice.ResetAction(null));

                onsubmitted();

                if (saveAndClose) {
                    //remove the close validation code = -1 for action which has just been fulfilled
                    let codes = ticket.CloseValidationCodes.filter(c => c !== -1);
                    closeTicket({ ...ticket, CloseValidationCodes: codes });

                }


            }).catch((e) => {
                //Probably a validation error
                //console.error('caught', e, ('Message' in e.data), ('title' in e.data), ('length' in e.data.errors?.Notes), e.data.errors.Notes[0]);
                (isObject(e.data) && 'title' in e.data) ?
                    (setApiError(e.data.title + ': ' + ('length' in e.data.errors?.Notes) ? e.data.errors.Notes.join(',') : 'unknonwn'))
                    : setApiError(e.data.toString())

                if (isObject(e.data) && 'Message' in e.data) setApiError(e.data.Message)

            })
            :
            updateAction(updateProps).unwrap().then(() => {
                //Reset the action in state
                dispatch(ActionSlice.ResetAction(null));
                console.log('onsubmitted', onsubmitted)
                onsubmitted();
            }).catch((e) => {
                //Probably a validation error
                console.error('caught', e)
                'Message' in e.data ?
                    setApiError(e.data.Message)
                    : setApiError(JSON.stringify(e.data))
            })
    }
    const externalUser = profile?.Features.External_Interface;
    const canSaveAndClose = !externalUser && ticket.Open_Status !== 0 && !actionId;
    const saveAndClose = () => {
        handleActionSubmit(true);
    }

    const saveButtonClicked = () => {
        handleActionSubmit(false);
    }

    return (
        <div>
            <div>
                <Card className='no-pop p-0 action-form'>
                    {showHeader && <CardHeader className='action-header' style={{ color: '#fff', background: profile?.Nav_Hex_Color }}>
                        <div className='d-flex align-items-center flex-row flex-grow justify-content-between'>
                            <div><SvgIcon icon={svg.trackChangesEnableIcon} /> New {profile?.Phrases.Action}</div>
                            {/*Link goes on the far right */}
                            <div>
                                <Link className='align-items-right' to={`./action`} title='Pop out' style={{ color: '#fff' }}>
                                    <Button fillMode='flat' size='medium' svgIcon={svg.windowRestoreIcon} className='off'></Button>
                                </Link>
                                <Button fillMode='flat' size='medium' svgIcon={svg.xIcon} title='close' onClick={onCloseClicked}></Button>
                            </div>
                        </div>
                    </CardHeader>}
                    <CardBody>
                        {/* <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseActionheader" aria-expanded="true" aria-controls="collapseActionheader">&lt;</button> */}
                        {(savingAction || fetchingAction || updatingAction) && <PartialSpinny />}
                        <Form ignoreModified={true} onSubmit={() => handleActionSubmit(false)} id={actionId ? 'edit-action-form' : formName}
                            render={(formRenderProps) =>
                                <FormElement  >
                                    <EffortExceededWarning ticket={ticket} actionDateUtc={new Date(newAction?.Action_Dt || 0)} time={newAction?.Billable_Time || 0 + newAction?.Non_Billable_Time || 0} profile={profile} />
                                    <div id='error-summary'>
                                        {/* Validation */}
                                        {formRenderProps.errors &&
                                            formRenderProps.errors.VALIDATION_SUMMARY && (
                                                <div className={"k-messagebox k-messagebox-error"}>
                                                    {formRenderProps.errors.VALIDATION_SUMMARY}
                                                </div>
                                            )}
                                        {apiError && <div className={"k-messagebox k-messagebox-error"}>
                                            {apiError}
                                        </div>}

                                    </div>
                                    <Field
                                        id={"action"}
                                        name={"action"}
                                        component={ActionNote}
                                        companyId={ticket?.Company_Id}
                                        statusId={ticket?.Call_Status_Id}
                                        ticketId={ticket?.ID}
                                        isParent={ticket.Is_Parent}
                                        visited={false}
                                    />

                                </FormElement>} />
                    </CardBody>
                    <CardFooter>
                        {/* file attachments */}
                        <div className=''>
                            {isFileActive && <div className={`inline-block col-12 align-top ms-3`}>
                                <FileUpload Primary_Id={actionId} FileType={profile?.Features.Self_Service ? FileType.Call_Action_External : FileType.Call_Action_Internal} />
                            </div>}
                            <div className='ms-auto p-1 d-flex flex-row-reverse col-12' >
                                {showSaveButton !== false &&
                                    (canSaveAndClose ?
                                        <SplitButton onButtonClick={saveButtonClicked} items={[{ text: 'Save and Close' }]} text='Save Action' onItemClick={saveAndClose}
                                            className='rev-primary-button save-action-button' imageUrl='./images/Log-Ticket-White.png' >
                                            Save {profile?.Phrases.Action} </SplitButton>
                                        : <Button form={actionId ? 'edit-action-form' : formName} type={"submit"} style={{ color: '#fff', background: profile?.Nav_Hex_Color }} className='save-action-button'>
                                            <img src='./images/Log-Ticket-White.png' className='inline mr-2 w-5' alt="Save Action" />
                                            Save {profile?.Phrases.Action}
                                        </Button>)}
                                {profile?.Features.AI_Assist && <AINotes />}
                                <div className='mr-2'><AddKBButton from='action' /></div>
                            </div>
                        </div>
                    </CardFooter>
                </Card>
            </div>

        </div>
    );
};

export default AddAction;